import React, { useState } from "react";
import {
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box,
  Divider,
  Chip,
} from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:4000";

const EXAMPLES = [
  "Bank security alert: caller presses 1 to verify their account, then enters their 8-digit card number",
  "IT helpdesk: employee presses 1 to reset password, then speaks their employee ID",
  "Payroll refund notice: caller presses 1 to confirm benefit, then enters their bank account number",
  "Rewards award scenario: caller presses 1, receives a 4 digit OTP by SMS, then enters the OTP followed by #",
  "Tax refund IVR: press 1 to claim refund, then enter PAN card number via keypad",
];

export default function CreateFlow({ token }) {
  const [flowName, setFlowName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/generate-flow`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ prompt, flowName }),
      });
      const data = await res.json();
      if (res.ok) {
        setResult(data);
        setPrompt("");
        setFlowName("");
      } else {
        setError((data.error || "Failed to generate flow.") + (data.details ? `\n\n${data.details}` : ""));
      }
    } catch {
      setError("Network error. Check your connection.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopySid = () => {
    if (result?.flowSid) {
      navigator.clipboard.writeText(result.flowSid);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="page-wrapper animate-in">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ color: "#0f172a", mb: 0.5 }}>AI Flow Builder</Typography>
        <Typography sx={{ color: "#64748b", fontSize: "0.95rem" }}>
          Describe your vishing scenario and AI will create and deploy the call flow automatically
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 3, flexWrap: { xs: "wrap", md: "nowrap" } }}>
        {/* Left - Form */}
        <Box sx={{ flex: "1 1 500px", maxWidth: 640 }}>
          <Box className="enterprise-card" sx={{ p: 4 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: "10px" }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {result && (
              <Box sx={{ mb: 3, p: 2.5, borderRadius: "12px", background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.2)" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <CheckCircleIcon sx={{ color: "#10b981", fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 700, color: "#065f46", fontSize: "0.95rem" }}>
                    Flow Created Successfully
                  </Typography>
                </Box>
                <Typography sx={{ color: "#065f46", fontSize: "0.85rem", mb: 1.5 }}>
                  <strong>{result.flowName}</strong> is now
                  <Chip label="published" size="small" sx={{ mx: 0.5, background: "#d1fae5", color: "#065f46", fontWeight: 600, fontSize: "0.7rem", height: 22 }} />
                  and ready to execute.
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", p: "10px 14px", borderRadius: "8px", background: "#f8fafc", border: "1px solid #e2e8f0", fontFamily: "monospace", fontSize: "0.82rem", color: "#334155", gap: 1 }}>
                  <Box sx={{ flex: 1, wordBreak: "break-all" }}>{result.flowSid}</Box>
                  <Button
                    size="small"
                    startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                    onClick={handleCopySid}
                    sx={{ textTransform: "none", color: "#6366f1", fontWeight: 600, minWidth: "auto", fontSize: "0.8rem" }}
                  >
                    {copied ? "Copied!" : "Copy"}
                  </Button>
                </Box>
              </Box>
            )}

            <form onSubmit={handleGenerate}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <Box>
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#334155", mb: 0.8 }}>Flow Name</Typography>
                  <TextField
                    fullWidth size="small" required
                    placeholder="e.g. Bank Security Alert"
                    value={flowName}
                    onChange={(e) => setFlowName(e.target.value)}
                    disabled={loading}
                  />
                </Box>

                <Box>
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#334155", mb: 0.8 }}>Scenario Description</Typography>
                  <TextField
                    fullWidth multiline rows={5} required
                    placeholder="Describe the vishing call flow you want to create..."
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    disabled={loading}
                  />
                </Box>

                {/* Example chips */}
                <Box>
                  <Typography sx={{ color: "#94a3b8", fontSize: "0.75rem", mb: 1 }}>
                    Quick examples — click to use:
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                    {EXAMPLES.map((ex, i) => (
                      <Chip
                        key={i}
                        label={ex.length > 50 ? ex.slice(0, 50) + "…" : ex}
                        size="small"
                        clickable
                        onClick={() => setPrompt(ex)}
                        disabled={loading}
                        sx={{
                          background: "rgba(99,102,241,0.06)",
                          border: "1px solid rgba(99,102,241,0.15)",
                          color: "#6366f1",
                          fontSize: "0.72rem",
                          "&:hover": { background: "rgba(99,102,241,0.12)" },
                        }}
                      />
                    ))}
                  </Box>
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading || !flowName.trim() || !prompt.trim()}
                  startIcon={loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <AutoFixHighIcon />}
                  sx={{ mt: 1, py: 1.4, fontSize: "0.95rem" }}
                >
                  {loading ? "Generating flow with AI…" : "Generate & Create Flow"}
                </Button>
              </Box>
            </form>
          </Box>
        </Box>

        {/* Right - Info */}
        <Box sx={{ flex: "0 0 270px", display: { xs: "none", md: "flex" }, flexDirection: "column", gap: 2 }}>
          <Box className="enterprise-card" sx={{ p: 2.5 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", color: "#0f172a", mb: 1.5 }}>
              AI-Powered Generation
            </Typography>
            <Typography sx={{ color: "#64748b", fontSize: "0.8rem", lineHeight: 1.6 }}>
              Our AI engine converts your natural language description into a fully functional IVR flow, deploying it instantly.
            </Typography>
          </Box>
          <Box className="enterprise-card" sx={{ p: 2.5 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", color: "#0f172a", mb: 1.5 }}>
              Tips for Best Results
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {["Be specific about keypad inputs", "Mention what data to capture", "Describe the caller's experience step-by-step"].map((tip, i) => (
                <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <Box sx={{ width: 5, height: 5, minWidth: 5, borderRadius: "50%", background: "#6366f1", mt: "7px" }} />
                  <Typography sx={{ fontSize: "0.8rem", color: "#64748b", lineHeight: 1.5 }}>{tip}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </div>
  );
}
